import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CornerUpLeft } from 'lucide-react';

import { cn } from '@/lib/utils';
import { getSurroundingMessages } from '../../../actions/teams';
import { withoutCustomerToken } from './customerLink';

const VIDEO_CHAT = /join my video chat|videowith\.me|video\.chekkit\.io/i;

const isPhoto = (attachment) =>
	attachment?.type?.startsWith('image/') || attachment?.url?.includes('giphy.com');

/**
 * The text shown for a quoted message, or for the message being replied to.
 * `customer` (the linked customer) hides an old "[Name]" token, and
 * `attachments`, when loaded, tell photos from other files.
 */
export const quotePreview = ({ text, attachmentCount, isDeleted, customer, attachments }) => {
	if (isDeleted) {
		return 'Original message was deleted';
	}

	const shown = withoutCustomerToken(text, customer);

	if (shown && VIDEO_CHAT.test(shown)) {
		return 'Video call';
	}

	if (shown) {
		return shown;
	}

	const count = attachments ? attachments.length : attachmentCount;

	if (count) {
		const noun = attachments?.every(isPhoto) ? 'photo' : 'attachment';
		return count > 1 ? `${count} ${noun}s` : noun[0].toUpperCase() + noun.slice(1);
	}

	return customer?.name ? `Linked ${customer.name}` : '';
};

const scrollToMessage = (_message) => {
	const element = document.getElementById(_message);

	if (!element) {
		return false;
	}

	element.scrollIntoView({ behavior: 'smooth', block: 'center' });
	element.classList.add('animate-pulse');
	setTimeout(() => element.classList.remove('animate-pulse'), 1500);

	return true;
};

/**
 * The message a reply quotes, shown the way Messages does: a small faded copy
 * of the original bubble, in its sender's colour, joined to the reply by a
 * hairline. The quoted message is used when it is loaded, so edits and deletes
 * show straight away, and the stored quote otherwise. Clicking jumps to it.
 */
const ReplyQuote = ({ message, isAuthenticatedUser }) => {
	const dispatch = useDispatch();
	const { replyTo } = message;

	const users = useSelector((state) => state.users);
	const _me = useSelector((state) => state.auth?.user?._id);
	const quoted = useSelector((state) =>
		state.teams.teamConversations
			.find((c) => c._id === message._teamConversation)
			?.teamMessages?.find((m) => m._id === replyTo._message)
	);

	const isDeleted = quoted ? !!quoted.isDeleted : !!replyTo.isDeleted;
	const _author = quoted?._user || replyTo._user;
	const isQuotingMe = _author && _author === _me;
	const author = users.find((u) => u._id === _author);

	const caption = isQuotingMe
		? isAuthenticatedUser
			? 'Replied to yourself'
			: 'Replied to you'
		: `Replied to ${author?.name?.split(' ')[0] || 'a message'}`;

	const preview = quotePreview({
		isDeleted,
		text: quoted ? quoted.text : replyTo.text,
		attachmentCount: replyTo.attachmentCount,
		attachments: quoted?.attachments,
		customer: quoted?._attachedCustomer,
	});

	const jumpToQuoted = async () => {
		if (isDeleted || scrollToMessage(replyTo._message)) {
			return;
		}

		await dispatch(
			getSurroundingMessages({
				_teamConversation: message._teamConversation,
				_teamMessage: replyTo._message,
			})
		);

		setTimeout(() => scrollToMessage(replyTo._message), 100);
	};

	return (
		<div
			className={cn(
				'flex max-w-full flex-col',
				isAuthenticatedUser ? 'items-end' : 'items-start'
			)}
		>
			<span className="mb-0.5 flex items-center gap-1 px-2 text-[11px] text-muted-foreground">
				<CornerUpLeft className="size-3" />
				{caption}
			</span>
			<button
				type="button"
				onClick={jumpToQuoted}
				disabled={isDeleted}
				title={isDeleted ? undefined : 'Show the original message'}
				className={cn(
					'max-w-full rounded-[16px] px-3 py-1.5 text-left text-[13px] leading-snug text-foreground/60 transition-opacity',
					isQuotingMe ? 'bg-bubble-team/70' : 'border border-border/60 bg-muted/50',
					isDeleted ? 'italic' : 'hover:text-foreground/80'
				)}
			>
				<span className="line-clamp-2 whitespace-pre-wrap break-words">{preview}</span>
			</button>
			{/* joins the quote to the reply below it */}
			<span
				aria-hidden="true"
				className={cn('h-1.5 w-px bg-border', isAuthenticatedUser ? 'mr-5' : 'ml-5')}
			/>
		</div>
	);
};

export default ReplyQuote;
